import { useState, type FormEvent } from 'react';
import { Flag, Pencil, Trash2, Check } from 'lucide-react';
import { DistanceUnit, Language, RunLogEntry } from '../types';
import { formatPace, formatTime, hmsToSeconds, kmPaceToMilePace, secondsToHms } from '../utils/calculator';
import { GoalSummary, RaceGoal, goalSummary } from '../utils/goal';
import { STANDARD_RACES } from '../utils/performance';
import { t, tf } from '../utils/i18n';

interface GoalCardProps {
  language: Language;
  goal: RaceGoal | null;
  logs: RunLogEntry[];
  distanceUnit: DistanceUnit;
  onChangeGoal: (goal: RaceGoal | null) => void;
}

const inputClass =
  'w-full min-h-12 px-4 rounded-2xl border-none outline-none bg-black/5 dark:bg-slate-800/80 text-base font-semibold text-slate-900 dark:text-white';

export function daysLeftText(language: Language, summary: GoalSummary): string {
  if (summary.daysLeft < 0) return t(language, 'goalPast');
  if (summary.daysLeft === 0) return t(language, 'goalToday');
  if (summary.daysLeft === 1) return t(language, 'goalOneDay');
  return tf(language, 'goalDaysLeft', { days: summary.daysLeft });
}

export function statusText(language: Language, summary: GoalSummary): string {
  switch (summary.status) {
    case 'ahead':
      return t(language, 'goalStatusAhead');
    case 'onTrack':
      return t(language, 'goalStatusOn');
    case 'behind':
      return tf(language, 'goalStatusBehind', { time: formatTime(Math.round(summary.gapSeconds ?? 0)) });
    default:
      return t(language, 'goalStatusUnknown');
  }
}

export function statusColor(status: GoalSummary['status']): string {
  if (status === 'ahead' || status === 'onTrack') return 'text-emerald-700 dark:text-emerald-300';
  if (status === 'behind') return 'text-amber-700 dark:text-amber-300';
  return 'text-slate-600 dark:text-slate-400';
}

export function raceName(language: Language, race: (typeof STANDARD_RACES)[number]): string {
  return race.labelKey ? t(language, race.labelKey) : (race.label ?? race.id);
}

/** Naam van de standaardafstand als die overeenkomt, anders de afstand in km. */
export function goalDistanceName(language: Language, distanceKm: number): string {
  const race = STANDARD_RACES.find((r) => Math.abs(r.km - distanceKm) < 0.01);
  return race ? raceName(language, race) : `${distanceKm} km`;
}

export default function GoalCard({ language, goal, logs, distanceUnit, onChangeGoal }: GoalCardProps) {
  const [editing, setEditing] = useState(false);
  const [raceId, setRaceId] = useState('10k');
  const [hms, setHms] = useState({ h: 0, m: 50, s: 0 });
  const [date, setDate] = useState('');
  const [error, setError] = useState(false);

  const startEditing = () => {
    if (goal) {
      const race = STANDARD_RACES.find((r) => Math.abs(r.km - goal.distanceKm) < 0.01);
      setRaceId(race?.id ?? '10k');
      const parts = secondsToHms(goal.targetSeconds);
      setHms({ h: parts.hours, m: parts.minutes, s: parts.seconds });
      setDate(goal.date);
    } else {
      setRaceId('10k');
      setHms({ h: 0, m: 50, s: 0 });
      setDate('');
    }
    setError(false);
    setEditing(true);
  };

  const save = (e: FormEvent) => {
    e.preventDefault();
    const race = STANDARD_RACES.find((r) => r.id === raceId);
    const targetSeconds = hmsToSeconds(hms.h, hms.m, hms.s);
    if (!race || targetSeconds < 60 || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      setError(true);
      return;
    }
    onChangeGoal({ distanceKm: race.km, targetSeconds, date });
    setEditing(false);
  };

  const summary = goal ? goalSummary(goal, logs) : null;
  const requiredPace = summary ? (distanceUnit === 'mi' ? kmPaceToMilePace(summary.requiredPace) : summary.requiredPace) : 0;

  return (
    <section className="rounded-3xl bg-black/5 dark:bg-white/5 p-4 space-y-3" aria-labelledby="goal-title">
      <div className="flex items-center justify-between gap-3">
        <h3 id="goal-title" className="flex items-center gap-2 text-base font-bold text-slate-900 dark:text-white">
          <Flag className="w-4 h-4 text-[#7C3AED]" />
          {t(language, 'goalTitle')}
        </h3>
        {goal && !editing && (
          <div className="flex gap-1">
            <button
              onClick={startEditing}
              aria-label={t(language, 'goalEdit')}
              className="w-11 h-11 rounded-full flex items-center justify-center text-slate-500 hover:bg-black/5 dark:hover:bg-white/10 cursor-pointer"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={() => onChangeGoal(null)}
              aria-label={t(language, 'goalDelete')}
              className="w-11 h-11 rounded-full flex items-center justify-center text-slate-500 hover:text-red-500 hover:bg-black/5 dark:hover:bg-white/10 cursor-pointer"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {editing ? (
        <form onSubmit={save} className="space-y-3">
          <label className="block space-y-1.5 text-sm font-medium text-slate-600 dark:text-slate-300">
            {t(language, 'goalRace')}
            <select value={raceId} onChange={(e) => setRaceId(e.target.value)} className={inputClass}>
              {STANDARD_RACES.map((race) => (
                <option key={race.id} value={race.id}>
                  {raceName(language, race)}
                </option>
              ))}
            </select>
          </label>
          <div className="space-y-1.5">
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{t(language, 'goalTargetTime')}</span>
            <div className="grid grid-cols-3 gap-2">
              {([['h', 'uiHrs'], ['m', 'uiMin'], ['s', 'uiSec']] as const).map(([field, key]) => (
                <input
                  key={field}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  aria-label={t(language, key)}
                  placeholder={t(language, key)}
                  value={hms[field]}
                  onChange={(e) => setHms({ ...hms, [field]: Math.max(0, parseInt(e.target.value) || 0) })}
                  className={`${inputClass} text-center font-mono`}
                />
              ))}
            </div>
          </div>
          <label className="block space-y-1.5 text-sm font-medium text-slate-600 dark:text-slate-300">
            {t(language, 'goalDate')}
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputClass} />
          </label>
          {error && <p className="text-sm text-amber-700 dark:text-amber-300">{t(language, 'goalInvalid')}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="flex-1 min-h-12 rounded-2xl bg-black/5 dark:bg-white/10 font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap cursor-pointer"
            >
              {t(language, 'cancelBtn')}
            </button>
            <button
              type="submit"
              className="flex-1 min-h-12 rounded-2xl bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer"
            >
              <Check className="w-4 h-4" />
              {t(language, 'goalSave')}
            </button>
          </div>
        </form>
      ) : goal && summary ? (
        <div className="space-y-3">
          <div className="flex items-end justify-between gap-3">
            <div>
              <div className="text-xl font-extrabold text-slate-900 dark:text-white">
                {goalDistanceName(language, goal.distanceKm)} · {formatTime(goal.targetSeconds, goal.targetSeconds >= 3600)}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400">{daysLeftText(language, summary)}</div>
            </div>
            <div className="text-right">
              <div className="text-sm text-slate-600 dark:text-slate-400">{t(language, 'goalRequiredPace')}</div>
              <div className="text-lg font-bold text-slate-900 dark:text-white whitespace-nowrap">
                {formatPace(requiredPace).replace(/^0/, '')} /{distanceUnit}
              </div>
            </div>
          </div>
          <div className="rounded-2xl bg-black/5 dark:bg-slate-800/80 px-4 py-3 space-y-0.5">
            <div className={`text-base font-bold ${statusColor(summary.status)}`}>{statusText(language, summary)}</div>
            {summary.predictedSeconds !== null && (
              <div className="text-sm text-slate-600 dark:text-slate-400">
                {t(language, 'goalPredicted')}: {formatTime(Math.round(summary.predictedSeconds), summary.predictedSeconds >= 3600)}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-slate-600 dark:text-slate-400">{t(language, 'goalEmpty')}</p>
          <button
            onClick={startEditing}
            className="min-h-12 px-5 rounded-2xl bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold whitespace-nowrap active:scale-95 transition-all duration-150 cursor-pointer"
          >
            {t(language, 'goalSet')}
          </button>
        </div>
      )}
    </section>
  );
}
